import {
  AlertCircleIcon,
  BracketsIcon,
  TableIcon,
} from '@langchain/untitled-ui-icons';

import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { Dialog, DialogContent } from '@/components/Dialog/Dialog';
import {
  type GroupedTabOption,
  GroupedTabs,
  Text,
} from '@/design-system/components';
import { Button } from '@/design-system/components/Button';
import { DatasetSchema, ResourceTag } from '@/types/schema';
import { useScrollParent } from '@/utils/VirtuosoCustomScrollParentContext/useScrollParent';
import { pluralize } from '@/utils/stringUtils';

import { CreateDatasetFormSection } from './DatasetFormSection';
import { AlwaysEnabledDatasetSchemaEditor } from './DatasetSchemaEditor';
import { FileUploadSection } from './FileUploadSection';
import { DatasetDetails } from './components/DatasetDetails';
import { DatasetRowPreview } from './components/DatasetRowPreview';
import { FileDatasetKeysSelector } from './components/FileDatasetKeysSelector';
import { SAMPLE_DATASET_INPUTS_OUTPUTS } from './constants';
import { useFileUploadTemplateStore } from './hooks/useFileUploadTemplateStore';
import { useDatasetSchemaStore } from './hooks/useSchemaStore';
import { inferSchemasFromFilePreview } from './utils';

function FormatPreview({ format }: { format: 'csv' | 'jsonl' }) {
  if (format === 'jsonl') {
    return (
      <div className="mb-4 max-w-full rounded-md border border-secondary bg-primary p-3">
        <pre className="max-w-full truncate font-mono text-xs leading-relaxed text-secondary">
          {`{"inputs_1": "${SAMPLE_DATASET_INPUTS_OUTPUTS.PHOTOSYNTHESIS.input}", "outputs_1": "${SAMPLE_DATASET_INPUTS_OUTPUTS.PHOTOSYNTHESIS.output}"}`}
          <br />
          {`{"inputs_1": "${SAMPLE_DATASET_INPUTS_OUTPUTS.GRAVITY.input}", "outputs_1": "${SAMPLE_DATASET_INPUTS_OUTPUTS.GRAVITY.output}"}`}
        </pre>
      </div>
    );
  }

  return (
    <div className="mb-4 flex rounded-md border border-secondary">
      <div className="w-1/2">
        <h4 className="rounded-tl-md bg-tertiary px-2 py-2 font-mono text-xs font-bold text-secondary">
          Input 1
        </h4>
        <p className="truncate px-2 py-2 font-mono text-xs text-disabled">
          {SAMPLE_DATASET_INPUTS_OUTPUTS.PHOTOSYNTHESIS.input}
        </p>
      </div>
      <div className="w-1/2 border-l border-secondary">
        <h4 className="rounded-tr-md bg-tertiary px-2 py-2 font-mono text-xs font-bold text-secondary">
          Output 1
        </h4>
        <p className="truncate px-2 py-2 font-mono text-xs text-disabled">
          {SAMPLE_DATASET_INPUTS_OUTPUTS.PHOTOSYNTHESIS.output}
        </p>
      </div>
    </div>
  );
}

export function FileUploadTemplateForm(props: {
  isEditing: boolean;
  tagsOnResource: ResourceTag[];
  setTagsOnResource: Dispatch<SetStateAction<ResourceTag[]>>;
  mode?: 'create' | 'addToExisting';
}) {
  const {
    isEditing,
    tagsOnResource,
    setTagsOnResource,
    mode = 'create',
  } = props;
  const [selectedFormat, setSelectedFormat] = useState<'csv' | 'jsonl'>('csv');

  const formatOptions: GroupedTabOption<'csv' | 'jsonl'>[] = [
    {
      display: 'CSV',
      value: 'csv',
      icon: <TableIcon className="h-3.5 w-3.5" />,
    },
    {
      display: 'JSONL',
      value: 'jsonl',
      icon: <BracketsIcon className="h-3.5 w-3.5" />,
    },
  ];

  // CSV Template State
  const fileName = useFileUploadTemplateStore((state) => state.fileName);
  const uploadError = useFileUploadTemplateStore((state) => state.error);
  const handleCSVFile = useFileUploadTemplateStore(
    (state) => state.handleCSVFile
  );
  const handleJSONLFile = useFileUploadTemplateStore(
    (state) => state.handleJSONLFile
  );
  const fileSize = useFileUploadTemplateStore((state) => state.fileSize);
  const filePreview = useFileUploadTemplateStore((state) => state.filePreview);
  const inputKeys = useFileUploadTemplateStore((state) => state.inputKeys);
  const clearFileUploadStore = useFileUploadTemplateStore(
    (state) => state.onCancel
  );
  const clearDatasetSchemaStore = useDatasetSchemaStore((state) => state.reset);

  return (
    <div className="flex h-full gap-4">
      <div className="shrink-1 h-full w-full min-w-[66%]">
        <div className="flex h-full flex-col items-stretch">
          <div className={`flex flex-col py-6 ${fileName ? '' : 'h-full'}`}>
            {fileName == null && (
              <div className="rounded-xl border border-tertiary px-6 py-4 shadow-sm">
                <div className="mb-4 flex items-start justify-between">
                  <div className="flex flex-1 items-center justify-between">
                    <Text variant="md" color="secondary">
                      Upload a <code>.csv</code> or <code>.jsonl</code> file
                      with clear input and output columns like this:
                    </Text>
                    <GroupedTabs
                      value={selectedFormat}
                      onChange={setSelectedFormat}
                      options={formatOptions}
                      size="sm"
                    />
                  </div>
                </div>

                <FormatPreview format={selectedFormat} />
                {mode === 'addToExisting' && (
                  <Text variant="md" color="secondary">
                    After uploading, you will be able to remap columns to match
                    your existing dataset structure.
                  </Text>
                )}
              </div>
            )}
            <div className={`${fileName ? '' : 'mt-6 flex-grow'}`}>
              <FileUploadSection
                fileName={fileName}
                error={uploadError ?? null}
                handleCSVFile={handleCSVFile}
                handleJSONLFile={handleJSONLFile}
                fileSize={fileSize}
                onClear={() => {
                  clearFileUploadStore();
                  clearDatasetSchemaStore();
                }}
              />
            </div>
          </div>

          {fileName && filePreview && inputKeys && !uploadError && (
            <>
              <FileUploadConfigurationSection
                isEditing={isEditing}
                tagsOnResource={tagsOnResource}
                setTagsOnResource={setTagsOnResource}
                mode={mode}
              />
              {mode === 'create' && (
                <div className="mt-6 flex flex-col">
                  <div className="flex items-center justify-between">
                    <Text variant="h3">
                      Dataset schema
                      <Text variant="xs" color="quaternary">
                        {' '}
                        (optional)
                      </Text>
                    </Text>
                    <SchemaInferenceButton />
                  </div>
                  <div className="mt-2 text-xs text-quaternary">
                    Optionally define input/output schemas or transformations
                    for this dataset. All data points in this dataset will be
                    validated against this schema.
                  </div>
                  <AlwaysEnabledDatasetSchemaEditor
                    hideChatModelSchemaOnboarding={true}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {fileName && (
        <div className="mt-6 hidden min-w-[34%] max-w-[34%] lg:flex">
          <DatasetRowPreview />
        </div>
      )}
    </div>
  );
}

function FileUploadConfigurationSection({
  isEditing,
  tagsOnResource,
  setTagsOnResource,
  mode = 'create',
}: {
  isEditing: boolean;
  tagsOnResource: ResourceTag[];
  setTagsOnResource: Dispatch<SetStateAction<ResourceTag[]>>;
  mode?: 'create' | 'addToExisting';
}) {
  return (
    <div className="flex flex-col gap-2">
      {mode === 'create' && (
        <DatasetDetails
          isEditing={isEditing}
          tagsOnResource={tagsOnResource}
          setTagsOnResource={setTagsOnResource}
        />
      )}
      <FileDatasetKeysSelector />
    </div>
  );
}

export function KVTemplateForm({
  isEditing,
  nonConformingExamples,
  existingDataset,
  setNonConformingPaneOpen,
  tagsOnResource,
  setTagsOnResource,
}: {
  isEditing: boolean;
  nonConformingExamples: string[];
  existingDataset?: DatasetSchema;
  setNonConformingPaneOpen: Dispatch<SetStateAction<boolean>>;
  tagsOnResource: ResourceTag[];
  setTagsOnResource: Dispatch<SetStateAction<ResourceTag[]>>;
}) {
  const containerRef = useScrollParent();

  useEffect(() => {
    if (
      containerRef &&
      containerRef.current &&
      nonConformingExamples &&
      nonConformingExamples.length > 0
    ) {
      containerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [containerRef, nonConformingExamples]);

  return (
    <div className="flex flex-col items-stretch gap-0">
      {nonConformingExamples &&
        nonConformingExamples.length > 0 &&
        existingDataset?.data_type === 'kv' && (
          <CreateDatasetFormSection
            name=""
            element={
              <div className="inline-flex min-w-full grow items-start justify-center gap-4 self-center rounded-xl border border-tertiary p-4 shadow">
                <div className="relative h-5 w-5 rounded-[20px]">
                  <div className="absolute left-[-4px] top-[-4px] h-7 w-7 rounded-3xl border-2 border-ls-orange-500 opacity-30" />
                  <div className="absolute left-[-9px] top-[-9px] h-[38px] w-[38px] rounded-3xl border-2 border-ls-orange-500 opacity-10" />
                  <div className="absolute left-0 top-0 h-5 w-5" />
                  <AlertCircleIcon className="absolute left-0 top-0 h-5 w-5 stroke-ls-orange-500" />
                </div>
                <div className="inline-flex shrink grow basis-0 flex-col items-start justify-start gap-3">
                  <div className="flex flex-col items-start justify-start gap-1 self-stretch">
                    <div className="shrink grow basis-0 pr-8 text-sm leading-tight">
                      Found <b>{nonConformingExamples?.length}</b> existing{' '}
                      {pluralize('example', nonConformingExamples?.length)} that
                      do not conform to your selected schema.
                    </div>
                    <div className="self-stretch text-sm font-normal leading-tight text-secondary">
                      You must update these existing{' '}
                      {pluralize('example', nonConformingExamples?.length)} to
                      match your new schema before saving.
                    </div>
                  </div>
                  <Button
                    variant="normal"
                    color="primary"
                    size="sm"
                    onClick={() => {
                      setNonConformingPaneOpen?.(true);
                    }}
                  >
                    Update Examples
                  </Button>
                </div>
              </div>
            }
            lg={true}
          />
        )}

      <CreateDatasetFormSection
        name="Dataset details"
        element={
          <DatasetDetails
            isEditing={isEditing}
            tagsOnResource={tagsOnResource}
            setTagsOnResource={setTagsOnResource}
          />
        }
        lg={true}
      />

      <CreateDatasetFormSection
        name={
          <>
            Dataset schema{' '}
            <span className="font-thin text-quaternary">(optional)</span>
          </>
        }
        sectionDescription="Add schema to enable syntax highlighting, validation, and automatic transformations when adding examples to this dataset"
        element={<AlwaysEnabledDatasetSchemaEditor />}
        lg={true}
      />
    </div>
  );
}

function SchemaInferenceButton() {
  // Get file preview data
  const filePreview = useFileUploadTemplateStore((state) => state.filePreview);
  const inputKeys = useFileUploadTemplateStore((state) => state.inputKeys);
  const outputKeys = useFileUploadTemplateStore((state) => state.outputKeys);
  const fileMimeType = useFileUploadTemplateStore(
    (state) => state.fileMimeType
  );
  const inputKeyMappings = useFileUploadTemplateStore(
    (state) => state.inputKeyMappings
  );
  const outputKeyMappings = useFileUploadTemplateStore(
    (state) => state.outputKeyMappings
  );

  // Get schema store actions
  const existingInputSchema = useDatasetSchemaStore(
    (state) => state.inputSchema
  );
  const existingOutputSchema = useDatasetSchemaStore(
    (state) => state.outputSchema
  );
  const setInputSchema = useDatasetSchemaStore((state) => state.setInputSchema);
  const setOutputSchema = useDatasetSchemaStore(
    (state) => state.setOutputSchema
  );

  // Modal state
  const [showConfirmationModal, setShowConfirmationDialog] = useState(false);

  const performSchemaInference = () => {
    if (!filePreview || (!inputKeys?.length && !outputKeys?.length)) {
      return;
    }

    const { inputSchema, outputSchema } = inferSchemasFromFilePreview(
      filePreview,
      inputKeys,
      outputKeys,
      fileMimeType === 'text/csv',
      inputKeyMappings,
      outputKeyMappings
    );

    setInputSchema(inputSchema);
    setOutputSchema(outputSchema);
  };

  const handleInferSchema = () => {
    if (!filePreview || (!inputKeys?.length && !outputKeys?.length)) {
      return;
    }

    const hasExistingSchema =
      (existingInputSchema != null &&
        Object.keys(existingInputSchema).length > 0) ||
      (existingOutputSchema != null &&
        Object.keys(existingOutputSchema).length > 0);

    if (hasExistingSchema) {
      setShowConfirmationDialog(true);
    } else {
      performSchemaInference();
    }
  };

  // Show button only when we have file preview data and keys
  const shouldShowButton =
    filePreview && (inputKeys?.length || outputKeys?.length);

  if (!shouldShowButton) {
    return null;
  }

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        size="sm"
        onClick={handleInferSchema}
        data-testid="infer-schema-button"
      >
        <Text variant="sm">Infer schemas from file</Text>
      </Button>

      <Dialog
        open={showConfirmationModal}
        onOpenChange={setShowConfirmationDialog}
      >
        <DialogContent title="Overwrite existing schema?">
          <div className="mb-4">
            <p className="text-sm text-secondary">
              You already have a schema defined. Are you sure you want to
              overwrite it with the inferred schema from the file?
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outlined"
              color="secondary"
              size="sm"
              onClick={() => setShowConfirmationDialog(false)}
            >
              Cancel
            </Button>
            <Button
              variant="normal"
              color="primary"
              size="sm"
              onClick={() => {
                performSchemaInference();
                setShowConfirmationDialog(false);
              }}
            >
              Overwrite
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
